import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock3, Users } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { sportsApi } from '@/services/api';
import { SportsAcademy } from '@/types';
import { EnquiryPanel } from '@/components/public/EnquiryPanel';
import { DetailPageSkeleton } from '@/components/public/DetailPageSkeleton';
import { PriceDisplay } from '@/components/public/PriceDisplay';

export const SportsAcademyDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [academy, setAcademy] = useState<SportsAcademy | null>(null);
  const [requestStatus, setRequestStatus] = useState<'loading' | 'success' | 'not-found'>('loading');
  const [requestSlug, setRequestSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setAcademy(null);
      setRequestSlug(null);
      setRequestStatus('not-found');
      return;
    }

    const controller = new AbortController();

    const fetchAcademy = async () => {
      setRequestSlug(slug);
      setRequestStatus('loading');
      setAcademy(null);
      try {
        const data = await sportsApi.getSportsBySlug(slug, controller.signal);
        setAcademy(data ?? null);
        setRequestStatus(data ? 'success' : 'not-found');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch sports academy:', error);
          setAcademy(null);
          setRequestStatus('not-found');
        }
      }
    };

    void fetchAcademy();
    return () => controller.abort();
  }, [slug]);

  if (slug && (requestSlug !== slug || requestStatus === 'loading')) {
    return <DetailPageSkeleton pageClassName="courses-page" />;
  }

  if (requestStatus === 'not-found' || !academy) {
    return (
      <section className="courses-page min-h-full">
        <div className="page-shell">
          <Button variant="ghost" size="sm" onClick={() => navigate('/sports')}>
            <ArrowLeft className="h-4 w-4" />
            Back to sports
          </Button>
          <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-950">Academy not found</h1>
        </div>
      </section>
    );
  }

  return (
    <section className="courses-page min-h-full">
      <div className="page-shell space-y-8">
        <section className="relative overflow-hidden rounded-[2rem] border border-white/60 bg-slate-950 shadow-[0_30px_80px_rgba(15,23,42,0.16)]">
          <img src={academy.coverImageUrl} alt={academy.name} className="h-72 w-full object-cover sm:h-80 lg:h-[26rem]" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/38 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 lg:p-10">
            <Button variant="ghost" size="sm" onClick={() => navigate('/sports')} className="mb-5 bg-white/10 text-white hover:bg-white/18 hover:text-white">
              <ArrowLeft className="h-4 w-4" />
              Back to sports
            </Button>
            <div className="flex flex-wrap gap-2">
              {(academy.sports || []).map((sport) => (
                <span key={sport} className="rounded-full border border-white/16 bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
                  {sport}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {academy.name}
            </h1>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="space-y-8">
            <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                Sports Overview
              </p>
              <div className="mt-5 divide-y divide-slate-200">
                <div className="pb-5">
                  <p className="whitespace-pre-line text-base leading-7 text-slate-700">{academy.about}</p>
                </div>
                <div className="grid gap-5 py-5 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Audience</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">
                      {academy.audience?.length ? academy.audience.join(', ') : 'Open to all interested learners'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Level</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">{academy.level || 'Open to All'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Teaching Mode</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">{academy.teachingMode || 'Offline'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Location</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">
                      {academy.area ? `${academy.area}, ` : ''}
                      {academy.city}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                    Programs
                  </p>
                  <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-950">
                    Batches and training options
                  </h2>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {(academy.programs || []).length} program{(academy.programs || []).length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="mt-5 space-y-4">
                {(academy.programs || []).length > 0 ? (
                  academy.programs.map((program, index) => (
                    <div key={`${program.name}-${index}`} className="rounded-[1.25rem] border border-slate-200 bg-slate-50/80 p-5">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-950">{program.name}</h3>
                          <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                            <Clock3 className="h-4 w-4" />
                            {program.schedule}
                          </p>
                          {program.ageGroup ? (
                            <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                              <Users className="h-4 w-4" />
                              Age group: {program.ageGroup}
                            </p>
                          ) : null}
                        </div>
                        <PriceDisplay isFree={academy.isFree} originalPrice={program.feeText} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-[1.25rem] border border-dashed border-slate-200 bg-slate-50/70 p-5 text-sm text-slate-600">
                    Program details will be announced soon.
                  </div>
                )}
              </div>
            </Card>

            <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                Facilities
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(academy.facilities || []).length > 0 ? (
                  academy.facilities.map((facility) => (
                    <span
                      key={facility}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700"
                    >
                      {facility}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-slate-600">Facilities will be updated shortly.</p>
                )}
              </div>
            </Card>
          </div>

          <div className="xl:pt-2">
            <div>
              <EnquiryPanel
                itemType="sports"
                itemId={academy.id}
                sectionLabel="Learning Guidance"
                ctaLabel="I'm interested"
                submitLabel="Submit"
                showNotesField={false}
                showPrivacyNotes={false}
                revealFieldsOnCta
                extraInfo={{
                  label: 'Starting Fee',
                  value: <PriceDisplay isFree={academy.isFree} originalPrice={academy.startingPriceText} variant="detail" />,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
